import DeleteIcon from '@mui/icons-material/Delete';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import {
    SelectChangeEvent,
    Select,
    MenuItem,
    AppBar,
    Autocomplete,
    Box,
    Button,
    Divider,
    FormHelperText,
    FormControl,
    IconButton,
    ListItem,
    ListItemText,
    TextField,
    Toolbar,
    Typography,
    useMediaQuery,
    type Theme,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { useFormik } from 'formik';
import { useMemo, useEffect } from 'react';
import { useAtom } from 'jotai';
import * as yup from 'yup';
import { Alert, EditAlert, Station, Criterion } from '../../../shared/types';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { useStations } from '../../hooks/useStations';
import { closedMixinToolbar, openedMixinToolbar } from '../helpers';
import { drawerStateAtom } from '../../atoms';
import { CriterionList } from '../CriterionList';
import { useUpdateAlert, useCreateAlert, useDeleteAlert } from '../../hooks/useAlerts';
import { useSimpleAlert } from '../../hooks/useSimpleAlert';
import { useNavigate } from 'react-router';

type Props = {
    alert?: Alert | null;
};

const initialValues = {
    description: '',
    to: 'mobile',
    active: true,
    station: null,
    criterion: [] as Criterion[],
    snooze: null,
} as EditAlert;

const criterionSchema = yup.object().shape({
    threshold: yup.number().required(),
    criteria: yup.object().shape({
        max: yup.number().required(),
        min: yup.number().required(),
        unit: yup.string().required(),
        value: yup.string().required(),
        text: yup.string().required(),
        label: yup.string().required(),
    }),
});

const validationSchema = yup.object().shape({
    description: yup.string().required('Required'),
    active: yup.boolean().required(),
    station: yup
        .object()
        .shape({
            name: yup.string().required('Required'),
            description: yup.string(),
            _id: yup.string(),
        })
        .required('Required'),
    criterion: yup.array().min(1, 'At least one critera is required').of(criterionSchema),
    to: yup.string().required('Required'),
    mobile: yup.string().when('to', {
        is: 'mobile',
        then: (schema) => schema.length(10, 'Must be a valid 10-digit mobile number').required('Required'),
        otherwise: (schema) => schema.nullable(),
    }),
    email: yup.string().when('to', {
        is: 'email',
        then: (schema) => schema.email('Must be a valid email address').required('Required'),
        otherwise: (schema) => schema.nullable(),
    }),
});

export const AlertForm = ({ alert = null }: Props) => {
    const [drawerState] = useAtom(drawerStateAtom);
    const isNotSm = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
    const { DeleteDialog, showDialog: showDeleteDialog } = useDeleteDialog();
    const { data: dataStations = [], isLoading: isLoadingStations } = useStations();
    const { mutate: updateAlert } = useUpdateAlert();
    const { mutate: createAlert } = useCreateAlert();
    const { mutate: deleteAlert } = useDeleteAlert();
    const { showSimpleAlert } = useSimpleAlert();
    const navigate = useNavigate();

    const onSubmit = async (values: EditAlert) => {
        const { _id, station, description, to, email, mobile, criterion, active, snooze } = values;
        const data = { station, description, to, email, mobile, criterion, active, snooze };

        if (_id) {
            updateAlert(
                { id: _id.toString(), data },
                {
                    onSuccess: () => showSimpleAlert({ title: 'Alert successfully updated' }),
                    onError: () => showSimpleAlert({ title: 'Error updating Alert', severity: 'error' }),
                },
            );
        } else {
            createAlert(data, {
                onSuccess: () => showSimpleAlert({ title: 'Alert successfully created' }),
                onError: () => showSimpleAlert({ title: 'Error creating Alert', severity: 'error' }),
            });
            navigate('/alerts');
        }
    };

    const formik = useFormik({ initialValues, validationSchema, onSubmit });

    const handleBack = () => navigate('/alerts');

    const handleShowDeleteDialog = () => {
        showDeleteDialog({ title: `Confirm DELETE of alert ${alert?.station.name}`, onDelete: () => handleDeleteAlert(alert) });
    };

    const handleActiveChange = (_event: React.MouseEvent<HTMLElement, MouseEvent>, active: boolean) => {
        formik.setFieldValue('active', active);
    };

    const handleStationSelection = (station: Station | null) => {
        formik.setFieldValue('station', station);
    };

    const handleCriterionChange = async (criterions: Criterion[]) => {
        await formik.setFieldValue('criterion', criterions);
    };

    const handleToChange = (event: SelectChangeEvent<'email' | 'mobile'>) => {
        formik.setFieldValue('to', event.target.value);
    };

    const handleDeleteAlert = async (alert: Alert | null) => {
        if (!alert) return;

        const id = alert._id.toString();

        deleteAlert(id, {
            onSuccess: () => {
                showSimpleAlert({ title: 'Alert successfully deleted' });
                navigate('/alerts');
            },
            onError: () => showSimpleAlert({ title: 'Error deleting Alert', severity: 'error' }),
        });
    };

    const stations = useMemo(() => {
        return dataStations.map(({ _id, name, stationId, description }) => ({ _id, name, stationId, description })) as Station[];
    }, [dataStations]);

    useEffect(() => {
        const values = alert ? { ...initialValues, ...alert } : initialValues;

        formik.resetForm({ values });
    }, [alert]);

    return (
        <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ maxWidth: '100%', p: 3, pb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 2 }}>
                        <Button variant="outlined" size="large" sx={{ minWidth: 0, pl: 1, pr: 1 }} onClick={handleBack} startIcon={<NavigateBeforeIcon />}>
                            Return to List
                        </Button>
                    </Box>
                    <Typography variant="h5" gutterBottom={false}>
                        {alert ? `Edit Alert for ${alert.station.name}` : 'Create Alert'}
                    </Typography>
                    <Divider />
                </Box>
                <Box
                    sx={{
                        height: '100%',
                        overflowY: 'scroll',
                    }}>
                    <form noValidate autoComplete="off">
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                maxWidth: (theme) => theme.breakpoints.values.sm,
                                p: 3,
                                mb: 8,
                            }}>
                            <Autocomplete
                                multiple={false}
                                options={stations ?? []}
                                filterSelectedOptions={true}
                                getOptionLabel={({ name }) => name}
                                getOptionKey={({ _id }: Station) => _id.toString()}
                                renderOption={(props: object, { name, description }: Station) => {
                                    return (
                                        <ListItem {...props}>
                                            <ListItemText primary={name} secondary={description} />
                                        </ListItem>
                                    );
                                }}
                                renderInput={(params) => <TextField {...params} variant="outlined" label="Station" error={Boolean(formik.errors.station)} helperText={formik.errors.station as string} />}
                                loading={isLoadingStations}
                                onChange={(_, station) => handleStationSelection(station)}
                                value={formik.values.station}
                                disabled={Boolean(alert?._id)}
                            />
                            <TextField
                                aria-describedby="description-text"
                                label="Description"
                                placeholder="description"
                                type="text"
                                name="description"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={Boolean(formik.errors.description)}
                                helperText={formik.errors.description}
                            />
                            <FormControl error={Boolean(formik.errors.criterion)}>
                                <CriterionList label="Criteria (all conditions have to be true for the alert to trigger)" severity="info" edit={true} onChange={handleCriterionChange} criterion={formik.values.criterion} />
                                {formik.errors.criterion && <FormHelperText>{formik.errors.criterion as string}</FormHelperText>}
                            </FormControl>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Typography sx={{ mt: 2 }}>Send to:</Typography>
                                <Box>
                                    <Select id="to" value={formik.values.to || 'mobile'} onChange={handleToChange}>
                                        <MenuItem value="mobile">Mobile</MenuItem>
                                        <MenuItem value="email">Email</MenuItem>
                                    </Select>
                                </Box>
                                {formik.values.to === 'mobile' && (
                                    <TextField
                                        aria-describedby="mobile-text"
                                        label="Mobile"
                                        placeholder="Mobile"
                                        type="text"
                                        name="mobile"
                                        value={formik.values.mobile}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.errors.mobile)}
                                        helperText={formik.errors.mobile}
                                    />
                                )}
                                {formik.values.to === 'email' && (
                                    <TextField
                                        aria-describedby="email-text"
                                        label="Email"
                                        placeholder="Email"
                                        type="text"
                                        name="email"
                                        value={formik.values.email}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.errors.email)}
                                        helperText={formik.errors.email}
                                    />
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Typography sx={{ mt: 2 }}>Status:</Typography>
                                <ToggleButtonGroup value={formik.values.active} exclusive onChange={handleActiveChange} aria-label="status">
                                    <ToggleButton color="success" value={true}>
                                        On
                                    </ToggleButton>
                                    <ToggleButton color="error" value={false}>
                                        Off
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                    </form>
                </Box>
            </Box>
            <AppBar position="fixed" sx={{ top: 'auto', bottom: 0, backgroundColor: (theme) => theme.palette.grey[50] }}>
                <Toolbar
                    sx={(theme) => ({
                        ...(isNotSm &&
                            drawerState && {
                                ...openedMixinToolbar(theme),
                            }),
                        ...(isNotSm &&
                            !drawerState && {
                                ...closedMixinToolbar(theme),
                            }),
                        maxWidth: (theme) => theme.breakpoints.values.sm,
                    })}>
                    {alert && (
                        <IconButton aria-label="delete" size="large" onClick={handleShowDeleteDialog}>
                            <DeleteIcon fontSize="inherit" />
                        </IconButton>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Button variant="contained" onClick={formik.submitForm} disabled={!formik.isValid || !formik.dirty}>
                        Save
                    </Button>
                </Toolbar>
            </AppBar>
            <DeleteDialog />
        </Box>
    );
};
